import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AssignmentsPageContainer,
  ContentWrapper,
  HeaderContainer,
  Title,
  ButtonWrapper,
  ButtonsContainer,
  FileLinkContainer,
  BaseLinkItem,
} from "./AssignmentsPage.styles";
import Sidebar from "../../components/Sidebar";
import Table from "../../components/Table";
import Button from "../../components/Buttons/Button";
import IconButton from "../../components/Buttons/IconButton";
import { fileIcon, videoFileIcon, linkIcon } from "assets/icons";
import {
  fetchAssignments,
  createAssignment,
  updateAssignment,
  deleteAssignment,
  fetchAssignmentById,
} from "../../services/assignmentService/assignmentService";
import { AssignmentRow } from "./AssignmentsPage.types";
import { AssignmentData } from "../../services/assignmentService/assignmentService.types";
import { ConfirmModal, CreateModal } from "../../components/Modals";
import { FileDisplay } from "../../components/FileToDisplay/FileToDisplay.types";
import { FileIcon } from "../../components/DragAndDropField/DragAndDropField.styles";
import DeletedRow from "../../components/DeletedRow";
import RestoredRow from "../../components/RestoredRow";
import { Tooltip } from "primereact/tooltip";
import { useSubscription } from "../../hooks/useSubscription";
import { useFileProgressPolling } from "../../hooks/useFileProgressPolling";
import { formatServerErrors } from "../../utils/errorHandler";

import { useNotify } from "context/NotificationContext";
import { logger } from "utils/logger";
import { useListPage } from "hooks/useListPage";
import { getServerErrorData } from "utils/httpError";
import { delIcon, editIcon } from "assets/icons";
const AssignmentsPage: React.FC = () => {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>();
  const { hasActiveSubscription, isLoading } = useSubscription();

  const {
    items: assignments,
    setItems: setAssignments,
    loading,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    handleSort,
    reload: loadAssignments,
  } = useListPage<AssignmentRow, Awaited<ReturnType<typeof fetchAssignments>>>({
    fetcher: ({ page, pageSize: size, orderBy }) =>
      fetchAssignments(Number(courseId), Number(lessonId), page, size, orderBy),
    select: (response) => ({
      items: response.results.map((assignment) => ({
        id: assignment.id,
        title: assignment.title,
        lesson: `Урок ${assignment.lesson_order}`,
        content: assignment.content,
        files_from_storage: assignment.files_from_storage || [],
        video_url: assignment.video_url,
        files: assignment.files || [],
      })),
      total: response.count,
    }),
    errorMessage: "Ошибка загрузки домашних заданий",
  });

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [assignmentToEdit, setAssignmentToEdit] = useState<AssignmentRow | null>(null);
  const [assignmentToDelete, setAssignmentToDelete] = useState<AssignmentRow | null>(null);

  const [storageFiles, setStorageFiles] = useState<FileDisplay[]>([]);

  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [recentlyRestored, setRecentlyRestored] = useState<number | null>(null);
  const [recentlyCreated, setRecentlyCreated] = useState<number | null>(null);
  const [recentlyEdited, setRecentlyEdited] = useState<number | null>(null);
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);

  const toast = useNotify();

  // Автообновление прогресса файлов в модальном окне
  useFileProgressPolling({
    isActive: isCreateModalOpen,
    entityId: assignmentToEdit?.id,
    filesFromStorage: storageFiles,
    updateFilesCallback: (files) => {
      // Создаем новые объекты для гарантии обновления React
      setStorageFiles(files.map(file => ({ ...file })));
    },
    fetchFunction: fetchAssignmentById,
    fetchParams: [Number(lessonId), assignmentToEdit?.id || 0],
  });




  const handleRemoveStorageFile = (fileId: number) => {
    setStorageFiles((prevFiles) =>
      (prevFiles || []).filter((file) => file.id !== fileId)
    );
  };

  const openCreateModal = () => {
    setStorageFiles([]);
    setAssignmentToEdit(null);
    setIsCreateModalOpen(true);
  };

  const openEditModal = (assignment: AssignmentRow) => {
    setStorageFiles(assignment?.files_from_storage || []);
    setAssignmentToEdit(assignment);
    setIsCreateModalOpen(true);
  };

  const handleDeleteAssignment = () => {
    if (assignmentToDelete) {
      const assignmentId = assignmentToDelete.id;
      setPendingDelete(assignmentId);
      setIsDeleteModalOpen(false);
      setAssignmentToDelete(null);

      deleteTimeoutRef.current = setTimeout(async () => {
        try {
          await deleteAssignment(
            Number(courseId),
            Number(lessonId),
            assignmentId
          );
          setPendingDelete(null);
          loadAssignments();
        } catch (error) {
          toast.show({
            severity: "error",
            summary: "Ошибка",
            detail: "Ошибка удаления задания",
            life: 3000
          })
          logger.error("Ошибка при удалении задания:", error);
        }
      }, 5000);
    }
  };

  const restoreAssignmentHandler = (assignmentId: number) => {
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
      deleteTimeoutRef.current = null;
    }

    setRecentlyRestored(assignmentId);
    setPendingDelete(null);

    loadAssignments();

    setTimeout(() => setRecentlyRestored(null), 3000);
  };

  const handleCreateOrEditAssignment = async (
    formData: Record<string, string | File[] | null | FileDisplay[]>,
    options?: { isEditing?: boolean }
  ) => {
    const { isEditing } = options || {};
    const title = typeof formData.title === 'string' ? formData.title : '';
    const content = typeof formData.content === 'string' ? formData.content : '';
    const video_url = typeof formData.video_url === 'string' ? formData.video_url : '';

    setExternalErrors({}); // Сброс ошибок
    setIsModalLoading(true); // Включаем индикатор загрузки
    
    try {
      // Получаем files_from_storage с полной информацией о типе файлов
      const filesFromStorage: FileDisplay[] = Array.isArray(formData.files_from_storage)
        ? (formData.files_from_storage as FileDisplay[])
        : Array.isArray(storageFiles)
        ? (storageFiles as FileDisplay[])
        : [];

      if (Array.isArray(formData.files_from_storage)) {
        setStorageFiles(formData.files_from_storage as FileDisplay[]);
      }

      const uploadedFiles: File[] = Array.isArray(formData.files)
        ? (formData.files as File[])
        : [];

      const assignmentData: AssignmentData = {
        lesson: Number(lessonId),
        title: title,
        content: content,
        video_url: video_url,
        files: uploadedFiles,
        files_from_storage: filesFromStorage,
      };

      if (isEditing && assignmentToEdit) {
        await updateAssignment(
          Number(courseId),
          Number(lessonId),
          assignmentToEdit.id,
          assignmentData
        );
        setRecentlyEdited(assignmentToEdit.id);
      } else {
        const createdAssignment = await createAssignment(
          Number(courseId),
          Number(lessonId),
          assignmentData
        );
        setRecentlyCreated(createdAssignment.id);
      }

      setStorageFiles([]);
      setIsCreateModalOpen(false);
      loadAssignments();

      setTimeout(() => {
        setRecentlyCreated(null);
        setRecentlyEdited(null);
      }, 3000);
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        const formattedErrors = formatServerErrors(serverErrors);

        setExternalErrors(formattedErrors);
      } else {
        logger.error("Ошибка сохранения задания:", error);
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Ошибка сохранения задания",
          life: 3000,
        });
      }
    } finally {
      setIsModalLoading(false); // Выключаем индикатор загрузки
    }
  };


  const getTableRows = () => {
    const rows = assignments.map((assignment, index) => {
      if (assignment.id === pendingDelete) {
        return {
          title: (
            <DeletedRow
              id={assignment.id}
              onRestore={restoreAssignmentHandler}
              colSpan={headers.length + 1}
              message={`Задание "${assignment.title}" будет удалено через 5 секунд.`}
              targetText="Восстановить задание"
            />
          ),
        };
      }
      
      // Создаем кнопки динамически с актуальным hasActiveSubscription
      const buttons = (
        <ButtonsContainer>
          {hasActiveSubscription && (
            <>
              <IconButton
                icon={editIcon}
                altText="Редактировать"
                onClick={() => openEditModal(assignment)}
              />
              <IconButton
                icon={delIcon}
                tone="danger"
                altText="Удалить"
                onClick={() => {
                  setAssignmentToDelete(assignment);
                  setIsDeleteModalOpen(true);
                }}
              />
            </>
          )}
        </ButtonsContainer>
      );
      
      return {
        title: assignment.title,
        lesson: assignment.lesson,
        files: (
          <FileLinkContainer>
            {assignment.video_url && (
              <>
                <BaseLinkItem 
                  className={`assignment-icon-link-${index}`}
                  data-pr-tooltip="Ссылка"
                >
                  <FileIcon as={linkIcon} aria-hidden="true" />
                </BaseLinkItem>
                <Tooltip target={`.assignment-icon-link-${index}`} />
              </>
            )}
            {assignment.files_from_storage && assignment.files_from_storage.length > 0 && (
              <>
                {assignment.files_from_storage.some((file) => file.type === "video") && (
                  <>
                    <BaseLinkItem 
                      className={`assignment-icon-video-${index}`}
                      data-pr-tooltip="Видео"
                    >
                      <FileIcon as={videoFileIcon} aria-hidden="true" />
                    </BaseLinkItem>
                    <Tooltip target={`.assignment-icon-video-${index}`} />
                  </>
                )}
                {assignment.files_from_storage.some((file) => file.type !== "video") && (
                  <>
                    <BaseLinkItem 
                      className={`assignment-icon-file-${index}`}
                      data-pr-tooltip="Файл"
                    >
                      <FileIcon as={fileIcon} aria-hidden="true" />
                    </BaseLinkItem>
                    <Tooltip target={`.assignment-icon-file-${index}`} />
                  </>
                )}
              </>
            )}
          </FileLinkContainer>
        ),
        buttons: buttons,
      };
    });

    if (recentlyRestored !== null) {
      const restoredIndex = assignments.findIndex(
        (a) => a.id === recentlyRestored
      );
      if (restoredIndex !== -1) {
        rows.splice(restoredIndex + 1, 0, {
          title: (
            <RestoredRow
              colSpan={headers.length + 1}
              message="Задание успешно восстановлено"
            />
          ),
        });
      }
    }

    if (recentlyCreated !== null) {
      const createdIndex = assignments.findIndex((a) => a.id === recentlyCreated);
      if (createdIndex !== -1) {
        rows.splice(createdIndex + 1, 0, {
          title: (
            <RestoredRow
              colSpan={headers.length + 1}
              message="Задание успешно создано"
            />
          ),
        });
      }
    }

    if (recentlyEdited !== null) {
      const editedIndex = assignments.findIndex((a) => a.id === recentlyEdited);
      if (editedIndex !== -1) {
        rows.splice(editedIndex + 1, 0, {
          title: (
            <RestoredRow
              colSpan={headers.length + 1}
              message="Задание успешно отредактировано"
            />
          ),
        });
      }
    }

    return rows;
  };

  const headers = [
    { label: "Задание", key: "title", fieldName: "title" },
    { label: "Урок", key: "lesson" },
    { label: "Материал", key: "files", fieldName: "files", hidden: true },
    { label: "", key: "buttons", sortable: false },
  ];

  const rowAmountOptions = [
    { label: "7 заданий", value: 7 },
    { label: "15 заданий", value: 15 },
    { label: "20 заданий", value: 20 },
  ];

  return (
    <AssignmentsPageContainer>
      <Sidebar />
      <ContentWrapper>
        <motion.div initial={{ height: "auto" }}>
          <HeaderContainer>
            <Title>Домашние задания</Title>
            {hasActiveSubscription && (
              <ButtonWrapper>
                <Button variant="primary" onClick={openCreateModal} size="lg">
                  Новое задание
                </Button>
              </ButtonWrapper>
            )}
          </HeaderContainer>
        </motion.div>
        <Table
          emptyState={{
            title: "Домашних заданий пока нет",
            description:
              "Задания, добавленные к урокам, будут собраны в этом списке.",
          }}
          headers={headers}
          rows={getTableRows()}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          onSort={handleSort}
          rowAmountOptions={rowAmountOptions}
          specialRowKey="title"
          loading={loading}
        />
        {isCreateModalOpen && (
          <CreateModal
            title={assignmentToEdit ? "Редактировать задание" : "Новое задание"}
            hasDragAndDrop={true}
            files_from_storage={storageFiles}
            onRemoveFile={handleRemoveStorageFile}
            submitButtonText={assignmentToEdit ? "Сохранить задание" : "Создать задание"}
            fields={[
              { name: "title", type: "text", placeholder: "Название", required: true },
              { name: "content", type: "textarea", placeholder: "Описание", required: true },
            ]}
            onSubmit={(formData) =>
              handleCreateOrEditAssignment(formData, { isEditing: !!assignmentToEdit })
            }
            onCancel={() => {
              setStorageFiles([]);
              setIsCreateModalOpen(false);
            }}
            initialValues={
              assignmentToEdit
                ? {
                  title: assignmentToEdit.title,
                  content: assignmentToEdit.content,
                  video_url: assignmentToEdit.video_url || "",
                }
                : {}
            }
            externalErrors={externalErrors}
            isLoading={isModalLoading}
          />
        )}
        {isDeleteModalOpen && (
          <ConfirmModal
            isOpen={isDeleteModalOpen}
            title="Удаление задания"
            message={`Вы уверены, что хотите удалить задание "${assignmentToDelete?.title}"?`}
            onConfirm={handleDeleteAssignment}
            onCancel={() => setIsDeleteModalOpen(false)}
          />
        )}
      </ContentWrapper>
    </AssignmentsPageContainer>
  );
};

export default AssignmentsPage;
